import React, { useEffect, useState } from 'react';
import {
  difference,
  every,
  flatten,
  includes,
  intersection,
  isArray,
  some,
  union,
} from 'lodash';
import { useIntl } from 'react-intl';
import SearchInput from '@rossum/components/search-input/SearchInput';
import { alpha, Box, Chip, List, Stack, Typography } from '@rossum/ui/material';
import { LocalizationKeys } from '../../../../i18n';
import Option from './Option';

export type OptionT<T extends number | string = number> = {
  label: string;
  value: T | Array<{ label: string; value: T }>;
};

type Props<T extends number | string> = {
  dataCy?: string;
  titleId?: LocalizationKeys;
  options: Array<OptionT<T>>;
  onSearch: (search: string) => void;
  onPick: (values: Array<T>) => void;
  isSearched: boolean;
  picked: Array<T>;
  noResultsPlaceholder?: React.ReactNode;
};

const Picker = <T extends number | string>({
  dataCy,
  isSearched,
  titleId,
  picked: propsPicked,
  options,
  onSearch,
  onPick,
  noResultsPlaceholder,
}: Props<T>) => {
  const intl = useIntl();
  const [search, setSearch] = useState('');
  useEffect(() => onSearch(search), [onSearch, search]);

  const optionValues = options.map((option: OptionT<T>) =>
    isArray(option.value)
      ? option.value.map(({ value }) => value)
      : option.value
  );
  const allValues = flatten(optionValues);
  const totalOptions = allValues.length;
  const picked = intersection(propsPicked, allValues);
  const allPicked = picked.length === totalOptions;

  return (
    <Stack
      flex="2"
      sx={{
        overflow: 'hidden',
        borderBottom: theme => `1px solid ${theme.palette.other.muted}`,
        '&:nth-of-type(2)': {
          flex: 3,
        },
      }}
    >
      <Stack
        spacing={1}
        sx={{
          mx: 1.5,
          pb: 1,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            mt: 1,
            minHeight: 29,
            height: 17,
          }}
        >
          <Stack
            direction="row"
            flex="1"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              pr: 1,
            }}
          >
            <Typography variant="body2" fontWeight="bold" lineHeight="1.1">
              {titleId
                ? intl.formatMessage({
                    id: titleId,
                  })
                : ''}
            </Typography>
            {totalOptions > 1 && (
              <span
                data-cy={dataCy}
                onClick={() => onPick(allPicked ? [] : allValues)}
              >
                <Typography
                  textAlign="right"
                  fontWeight="bold"
                  sx={{
                    cursor: 'pointer',
                    fontSize: 13,
                    transition: 'color 0.2s',
                    ml: 1.5,
                    color: theme => theme.palette.text.disabled,
                    '&:hover': {
                      color: theme => alpha(theme.palette.text.disabled, 0.6),
                    },
                  }}
                >
                  {intl.formatMessage({
                    id: allPicked
                      ? 'components.picker.deselectAll'
                      : 'components.picker.selectAll',
                  })}
                </Typography>
              </span>
            )}
          </Stack>
          {!!totalOptions && (
            <Chip label={`${picked.length} / ${totalOptions}`} />
          )}
        </Stack>
        <SearchInput
          onChange={setSearch}
          value={search}
          sx={{ width: '100%' }}
        />
      </Stack>
      {totalOptions ? (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            overflowY: 'auto',
            '& .collapsibleOption:hover': {
              backgroundColor: t => t.palette.action.hover,
            },
          }}
        >
          <List dense disablePadding>
            {options.map(({ label, value }: OptionT<T>) => {
              const isNested = Array.isArray(value);
              const values = isNested ? value.map(v => v.value) : [value];
              const semiChecked = some(values, val => includes(picked, val));
              const checked =
                !!picked.length && every(values, val => includes(picked, val));
              const key = values.join('');

              return (
                <Option
                  key={key}
                  id={key}
                  label={label}
                  childrenOptions={isNested ? value : undefined}
                  onChange={() =>
                    onPick(
                      checked
                        ? difference(picked, values)
                        : union(picked, values)
                    )
                  }
                  onPick={onPick}
                  picked={picked}
                  checked={checked}
                  semiChecked={semiChecked}
                  isSearched={isSearched}
                />
              );
            })}
          </List>
        </Box>
      ) : (
        noResultsPlaceholder
      )}
    </Stack>
  );
};

export default Picker;
